import React, { useState, useMemo } from 'react';
import InputField from 'components/form/input_field/InputField';
import Button from 'components/general/button/Button';
import FormInput from 'components/general/form_input/form_input';
import { Size as TooltipSize } from 'components/general/tooltip/tooltip';
import Checkbox from 'components/form/checkbox/checkbox';

interface PasswordStepProps {
  email: string;
  jobId: string;
  onPrevious: () => void;
}

const PasswordStep: React.FC<PasswordStepProps> = ({ email, jobId, onPrevious }) => {
  const styles = require('./Step.module.scss');
  const authenticityToken =
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [tosConsent, setTosConsent] = useState(false);
  const [tosError, setTosError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPasswordValid = useMemo(() => password.trim().length >= 8, [password]);
  const disableNext = isSubmitting || !isPasswordValid || !tosConsent;

  const handleSubmit = (e: React.FormEvent) => {    
    if (!tosConsent) {
      setTosError('Please accept the Terms & Conditions');
      return;
    }

    if (disableNext) return;

    setIsSubmitting(true);
  };

  return (
    <form
      action={window.Routes.finalize_user_job_ad_quick_applications_url(jobId)}
      method="POST"
      onSubmit={handleSubmit}
      className={styles.form}
    >
      <input type="hidden" name="authenticity_token" value={authenticityToken} />
      <input type="hidden" name="profile[unconfirmed_email]" value={email} />

      <div className={styles.form}>
        <FormInput
          name="password"
          label="Set your password"
          tooltip={{
            content: 'Set a password to later access your application and track your progress.',
            size: TooltipSize.Medium,
            iconColor: 'tuftsBlueBase',
          }}
          required
        >
          <InputField
            id="profile_password"
            name="profile[password]"
            placeholder="Type your password"
            type="password"
            value={password}
            hint="Your password must be at least 8 characters"
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError('');
            }}
            onBlur={() => {
              if (password.trim().length < 8) {
                setPasswordError('Password must be at least 8 characters long');
              } else if (passwordError) {
                setPasswordError('');
              }
            }}
            required
            error={passwordError || undefined}
          />
        </FormInput>

        <Checkbox
          id="profile_tos_consent"
          name="profile[tos_consent]"
          controlled={false}
          value="true"
          className={styles.checkbox}
          checked={tosConsent}
          color="tuftsBlue"
          onChange={(event) => {
            setTosConsent(event.target.checked);
            if (tosError) setTosError('');
          }}
          label={
            <span className={styles.consentLabel}>
              I agree to the{' '}
              <a href="/tos" target="_blank" className={styles.consentLink}>
                Terms
              </a>
              {' and '}
              <a href="/privacy" target="_blank" className={styles.consentLink}>
                Privacy Policy
              </a>
            </span>
          }
          error={tosError || undefined}
        />
      </div>

      <div className={styles.footer}>
        <Button
          onClick={onPrevious}
          type="Secondary"
          size="M"
          iconLeftName="chevronLeft"
          className={styles.actionButton}
        >
          Previous
        </Button>
      
        <Button
          typeAttr="submit"
          disabled={disableNext}
          size="M"
          className={styles.actionButton}
        >
          Apply
        </Button>
      </div>
    </form>
  );
};

export default PasswordStep;
